/**
 * External dependencies
 */
import type { ExperimentId } from '@nab/types';
import type { Attributes } from '../../../../../packages/conversion-action-library/form-submission/types';

/**
 * Internal dependencies
 */
import { getGA4ClientId } from '../ga4';
import { getCookie } from '../../utils/cookies';
import { domReady, getClosest, log } from '../../utils/helpers';
import { debounce } from '../../utils/lodash';
import { getAllActiveSegments } from '../../utils/segmentation';
import { getUniqueViews } from '../../utils/tracking';
import { getSettings } from '../../utils/helpers/internal/get-settings';

import type { Convert, ConvertibleAction } from '../../types';

const FORM_ACTIONS = [
	'nab/form-submission',
	'nab/wc-order',
	'nab/edd-order',
	'nab/surecart-order',
];

const JS_FORM_TYPES = [ 'nab_hubspot_form' ];

export function prepareFormForSyncingEventInWordPress(
	allActions: ReadonlyArray< ConvertibleAction >
): void {
	const actions = allActions.filter( isWordPressFormSubmissionAction );
	if ( ! actions.length ) {
		return;
	}
	addListenersToAddHiddenFields( actions );
}

export function listenToJavaScriptFormSubmission(
	allActions: ReadonlyArray< ConvertibleAction >,
	convert: Convert
): void {
	addHubSpotListener(
		allActions.filter( isHubSpotFormSubmissionAction ),
		convert
	);
	addHubSpotLegacyListener(
		allActions.filter( isHubSpotFormSubmissionAction ),
		convert
	);
}

// ====================
// JAVASCRIPT LISTENERS
// ====================

// -------
// HUBSPOT
// -------

function addHubSpotListener(
	actions: ReadonlyArray< ConvertibleAction< Attributes > >,
	convert: Convert
): void {
	if ( ! actions.length ) {
		return;
	}

	window.addEventListener(
		'hs-form-event:on-submission:success',
		( event ) => {
			if ( ! isHubSpotEvent( event ) ) {
				return;
			}
			convert(
				actions.filter(
					( action ) =>
						action.attributes.formName === event.detail.formId
				)
			);
		}
	);
}

function addHubSpotLegacyListener(
	actions: ReadonlyArray< ConvertibleAction< Attributes > >,
	convert: Convert
): void {
	if ( ! actions.length ) {
		return;
	}

	window.addEventListener( 'message', ( event ) => {
		if ( ! isHubSpotLegacyEvent( event ) ) {
			return;
		}

		if ( event.data.eventName !== 'onBeforeFormSubmit' ) {
			return;
		}

		convert(
			actions.filter(
				( action ) => action.attributes.formName === event.data.id
			)
		);
	} );
}

const isHubSpotEvent = (
	event: unknown
): event is CustomEvent< {
	readonly formId: string;
} > =>
	!! event &&
	'object' === typeof event &&
	'detail' in event &&
	!! event.detail &&
	'object' === typeof event.detail &&
	'formId' in event.detail &&
	'string' === typeof event.detail.formId &&
	!! event.detail.formId;

const isHubSpotLegacyEvent = ( event: {
	data: unknown;
} ): event is MessageEvent< {
	readonly type: 'hsFormCallback';
	readonly eventName: string;
	readonly id: string;
} > =>
	!! event.data &&
	'object' === typeof event.data &&
	'type' in event.data &&
	event.data.type === 'hsFormCallback';

// =======
// HELPERS
// =======

const isWordPressFormSubmissionAction = (
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > =>
	FORM_ACTIONS.includes( action.type ) &&
	! isJavaScriptFormSubmissionAction( action );

function isJavaScriptFormSubmissionAction(
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > {
	return (
		isFormSubmissionAction( action ) &&
		JS_FORM_TYPES.includes( action.attributes.formType )
	);
}

const isHubSpotFormSubmissionAction = (
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > =>
	isJavaScriptFormSubmissionAction( action ) &&
	action.attributes.formType === 'nab_hubspot_form';

const isFormSubmissionAction = (
	action: ConvertibleAction
): action is ConvertibleAction< Attributes > =>
	action.type === 'nab/form-submission';

const isJQuery = ( x?: unknown ): x is JQuery =>
	!! x && typeof x === 'function' && 'ajaxPrefilter' in x;

type JQuery = {
	readonly ajaxPrefilter: (
		handler: ( opts: unknown, originalOpts: unknown ) => void
	) => void;
};

const hasData = ( x?: unknown ): x is { data: unknown } =>
	!! x && 'object' === typeof x && 'data' in x;

const hasAction = ( x?: unknown ): x is { action: unknown } =>
	!! x && 'object' === typeof x && 'action' in x;

function addListenersToAddHiddenFields(
	actions: ReadonlyArray< ConvertibleAction< Attributes > >
) {
	const maybeAddHiddenFieldsInForm = debounce(
		( ev: Event ) => {
			const target = ev.target as HTMLElement | null;
			if ( ! target ) {
				return;
			}

			const form = getClosest< HTMLFormElement >( target, 'form' );
			if ( ! form ) {
				return;
			}

			generateCloudFields( actions ).forEach( ( [ key, value ] ) =>
				maybeAddHiddenField( form, key, value )
			);
		},
		1000,
		{ leading: true }
	);

	document.addEventListener( 'click', maybeAddHiddenFieldsInForm );
	document.addEventListener( 'click', maybeAddHiddenFieldsInForm, true );
	document.addEventListener( 'keyup', maybeAddHiddenFieldsInForm );
	document.addEventListener( 'keyup', maybeAddHiddenFieldsInForm, true );

	domReady( () => {
		/* eslint-disable */
		const jQuery = ( window as any ).jQuery;
		/* eslint-enable */
		if ( ! isJQuery( jQuery ) ) {
			return;
		}

		try {
			jQuery.ajaxPrefilter( ( opts, oriOpts ) => {
				if ( ! hasData( oriOpts ) || ! hasAction( oriOpts.data ) ) {
					return;
				}
				if ( 'nf_ajax_submit' !== oriOpts.data.action ) {
					return;
				}

				if ( ! hasData( opts ) || 'string' !== typeof opts.data ) {
					return;
				}
				opts.data += generateCloudFields( actions )
					.map(
						( [ key, value ] ) =>
							`&${ key }=${ encodeURIComponent( value ) }`
					)
					.join( '' );
			} );
		} catch ( e ) {
			log( e );
		}
	} );
}

function maybeAddHiddenField(
	form: HTMLFormElement,
	fieldName: string,
	fieldValue: string | undefined
): void {
	if ( ! fieldValue ) {
		return;
	}

	const hiddenField = getHiddenField( form, fieldName );
	hiddenField.setAttribute( 'value', fieldValue );
}

function getHiddenField(
	form: HTMLFormElement,
	fieldName: string
): HTMLElement {
	let hiddenField = form.querySelector< HTMLElement >(
		`input[name=${ fieldName }]`
	);
	if ( hiddenField ) {
		return hiddenField;
	}

	hiddenField = document.createElement( 'input' );
	hiddenField.setAttribute( 'type', 'hidden' );
	hiddenField.setAttribute( 'name', fieldName );

	form.appendChild( hiddenField );

	return hiddenField;
}

function generateCloudFields(
	actions: ReadonlyArray< ConvertibleAction< Attributes > >
): ReadonlyArray< [ string, string ] > {
	const settings = getSettings();
	return Object.entries( {
		nab_experiments_with_page_view:
			generateExperimentsWithPageViewValue( actions ),
		nab_segments: generateExperimentSegments(),
		nab_unique_views: generateUniqueViewsValue(),
		...( settings?.isGA4Integrated && {
			nab_ga4_client_id: getGA4ClientId(),
		} ),
	} ).filter( ( [ , value ] ) => !! value );
}

function generateExperimentsWithPageViewValue(
	actions: ReadonlyArray< ConvertibleAction< Attributes > >
) {
	const experiments = getExperimentsWithPageView();
	const relevantActions = actions.filter( ( { experiment } ) =>
		experiments.includes( experiment )
	);
	return relevantActions
		.map(
			( { experiment, alternative } ) =>
				`${ experiment }:${ alternative }`
		)
		.join( ';' );
}

function generateExperimentSegments() {
	return Object.entries( getAllActiveSegments() )
		.map( ( [ e, ss = [] ] ) => `${ e }:${ ss.join( ',' ) }` )
		.join( ';' );
}

function generateUniqueViewsValue() {
	const uniqueViews = getUniqueViews();
	return Object.entries( uniqueViews )
		.map( ( [ key, value ] ) => `${ key }:${ value }` )
		.join( ';' );
}

function getExperimentsWithPageView(): ReadonlyArray< ExperimentId > {
	try {
		const value = JSON.parse(
			getCookie( 'nabExperimentsWithPageViews' ) ?? ''
		) as Partial< Record< ExperimentId, number > >;
		return Object.keys( value ).map(
			( id ) => ( Number.parseInt( id ) || 0 ) as ExperimentId
		);
	} catch {
		return [];
	}
}
